'use client';

import { startTransition, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';

import { useUiStoreSelector } from '@/data';

const REFRESH_DEBOUNCE_MS = 500;

export const STORE_COOKIE_CHANGED_EVENT = 'gap:store-cookie-changed';

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let pendingStoreId: string | null = null;

const clearScheduledRefresh = () => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
};

const scheduleStoreSliceRefresh = (refresh: () => void) => {
  clearScheduledRefresh();

  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    pendingStoreId = null;
    startTransition(refresh);
  }, REFRESH_DEBOUNCE_MS);
};

export const notifyStoreCookieChanged = (storeId: string) => {
  if (!storeId) {
    return;
  }

  pendingStoreId = storeId;

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(STORE_COOKIE_CHANGED_EVENT, { detail: storeId }),
    );
  }
};

export const StoreCookieRefreshSync = () => {
  const router = useRouter();
  const { is_store_location_open } = useUiStoreSelector([
    'is_store_location_open',
  ]);
  const refreshRef = useRef<() => void>(() => {
    router.refresh();
  });

  refreshRef.current = () => {
    router.refresh();
  };

  useEffect(() => {
    if (is_store_location_open) {
      clearScheduledRefresh();
      return;
    }

    if (!pendingStoreId) {
      return;
    }

    scheduleStoreSliceRefresh(() => {
      refreshRef.current();
    });
  }, [is_store_location_open]);

  useEffect(() => {
    const onCookieChanged = () => {
      if (!pendingStoreId || is_store_location_open) {
        return;
      }

      scheduleStoreSliceRefresh(() => {
        refreshRef.current();
      });
    };

    window.addEventListener(STORE_COOKIE_CHANGED_EVENT, onCookieChanged);

    return () => {
      window.removeEventListener(STORE_COOKIE_CHANGED_EVENT, onCookieChanged);
      clearScheduledRefresh();
    };
  }, [is_store_location_open]);

  return null;
};
